import {useMemo, useState} from 'react';
import {PlanSummary} from '@tinct/web-components';
import {ReadQuota, ReadSubscription} from '@tinct/core-api-models';
import {formatPrice} from '../../../hooks/use-plan-options';

export type CompactPlanSummaryProps = {
  planName: string;
  isPro: boolean;
  badgeStatusVariant: 'green' | 'red' | 'yellow';
  badgeStatusLabel: string;
  creditQuota: ReadQuota | undefined;
  lpQuota: ReadQuota | undefined;
  subscription: ReadSubscription | null | undefined;
};

export function CompactPlanSummary({
  planName,
  isPro,
  badgeStatusVariant,
  badgeStatusLabel,
  creditQuota,
  lpQuota,
  subscription,
}: CompactPlanSummaryProps) {
  const unitAmountInCents = subscription?.items?.[0]?.price?.unitAmountInCents ?? 0;
  const currency = subscription?.items?.[0]?.price?.currency ?? 'usd';

  const recurringInterval = subscription?.items?.[0]?.price?.recurringInterval ?? 'year';
  const priceLabel = recurringInterval.charAt(0).toUpperCase() + recurringInterval.slice(1) + 'ly';

  // Read the two dates once so each memo below reads exactly what it lists as a
  // dependency. Reaching through `subscription` inside the body while depending
  // only on its fields leaves the two out of step, which the compiler refuses.
  const currentPeriodStart = subscription?.currentPeriodStart;
  const currentPeriodEnd = subscription?.currentPeriodEnd;

  const usagePeriod = useMemo(() => {
    if (!currentPeriodStart || !currentPeriodEnd) return undefined;
    const start = new Date(currentPeriodStart);
    const end = new Date(currentPeriodEnd);
    const fmt = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(start)} - ${fmt(end)}`;
  }, [currentPeriodStart, currentPeriodEnd]);

  // Reading the clock during render is impure — the countdown would drift with
  // whatever unrelated re-render happened to come next. Sampling it once when the
  // modal mounts is both pure and what the display already assumed, since nothing
  // here re-renders on a timer.
  const [now] = useState(() => Date.now());

  const usagePeriodReset = useMemo(() => {
    if (!currentPeriodEnd) return undefined;
    const end = new Date(currentPeriodEnd);
    const diffDays = Math.ceil((end.getTime() - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return undefined;
    return `Reset in ${diffDays} days`;
  }, [currentPeriodEnd, now]);

  return (
    <PlanSummary
      compact
      planName={planName}
      isPro={isPro}
      badgeStatusVariant={badgeStatusVariant}
      badgeStatusLabel={badgeStatusLabel}
      usageMetrics={[
        {
          label: 'credits left',
          value: creditQuota?.remainingQuota ?? 0,
          percent: creditQuota?.quotaLimit ? Math.floor(((creditQuota.quotaLimit - (creditQuota.remainingQuota ?? 0)) / creditQuota.quotaLimit) * 100) : 0,
        },
        {
          label: 'page variants left',
          value: lpQuota?.remainingQuota ?? 0,
          percent: lpQuota?.quotaLimit ? Math.floor(((lpQuota.quotaLimit - (lpQuota.remainingQuota ?? 0)) / lpQuota.quotaLimit) * 100) : 0,
        },
      ]}
      price={formatPrice(unitAmountInCents, currency)}
      priceLabel={priceLabel.toLowerCase()}
      included={[
        { value: String(creditQuota?.quotaLimit ?? 0), label: 'credits' },
        { value: String(lpQuota?.quotaLimit ?? 0), label: 'page variants' },
      ]}
      subscriptionDate={subscription?.startDate ? new Date(subscription.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
      usagePeriod={usagePeriod}
      usagePeriodReset={usagePeriodReset}
    />
  );
}
